// Progressive enhancement: native HTML5 drag-and-drop row reordering for any
// table marked with [data-reorderable]. On drop it posts the new order to the
// table's [data-reorder-url] and reverts the DOM if the save fails.
// window.ifunds.strings and window.toastr are provided globally (see main.ts).

import { ANTI_FORGERY_TOKEN_NAME, getAntiForgeryToken } from './antiForgery';

async function saveOrder(url: string, orderedIds: string[]): Promise<boolean> {
  const body = new URLSearchParams();
  body.append(ANTI_FORGERY_TOKEN_NAME, getAntiForgeryToken());
  orderedIds.forEach(id => body.append('orderedIds', id));
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    return response.ok;
  } catch {
    return false;
  }
}

function renumber(tbody: HTMLTableSectionElement): void {
  Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr')).forEach((row, index) => {
    const cell = row.querySelector('.number-cell');
    if (cell) {
      cell.textContent = (index + 1).toString();
    }
  });
}

function clearIndicators(tbody: HTMLTableSectionElement): void {
  tbody.querySelectorAll('.drag-over-top, .drag-over-bottom')
    .forEach(el => el.classList.remove('drag-over-top', 'drag-over-bottom'));
}

function orderedIdsOf(tbody: HTMLTableSectionElement): string[] {
  return Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'))
    .map(row => row.getAttribute('data-id') ?? '')
    .filter(id => id !== '');
}

function enhanceTable(table: HTMLTableElement): void {
  const tbody = table.querySelector('tbody');
  const url = table.getAttribute('data-reorder-url');
  if (!tbody || !url) {
    return;
  }

  let draggedRow: HTMLTableRowElement | null = null;
  let isSaving = false;

  tbody.addEventListener('dragstart', (e: DragEvent) => {
    const handle = (e.target as HTMLElement).closest('.drag-handle');
    if (!handle) {
      e.preventDefault();
      return;
    }
    draggedRow = handle.closest('tr');
    if (!draggedRow?.getAttribute('data-id')) {
      e.preventDefault();
      draggedRow = null;
      return;
    }
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', draggedRow.getAttribute('data-id') ?? '');
    }
  });

  tbody.addEventListener('dragover', (e: DragEvent) => {
    if (!draggedRow) {
      return;
    }
    e.preventDefault();
    const row = (e.target as HTMLElement).closest('tr');
    if (!row || row === draggedRow || row.parentElement !== tbody) {
      return;
    }
    const rect = row.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    clearIndicators(tbody);
    row.classList.add(after ? 'drag-over-bottom' : 'drag-over-top');
  });

  tbody.addEventListener('dragleave', (e: DragEvent) => {
    if (!tbody.contains(e.relatedTarget as Node)) {
      clearIndicators(tbody);
    }
  });

  tbody.addEventListener('drop', async (e: DragEvent) => {
    if (!draggedRow || isSaving) {
      return;
    }
    e.preventDefault();
    const target = (e.target as HTMLElement).closest('tr');
    clearIndicators(tbody);
    const moved = draggedRow;
    draggedRow = null;
    if (!target || target === moved || target.parentElement !== tbody) {
      return;
    }

    const originalOrder = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
    const rect = target.getBoundingClientRect();
    if (e.clientY > rect.top + rect.height / 2) {
      target.after(moved);
    } else {
      target.before(moved);
    }
    renumber(tbody);

    isSaving = true;
    const ok = await saveOrder(url, orderedIdsOf(tbody));
    isSaving = false;
    if (ok) {
      window.toastr.success(window.ifunds.strings.orderSaved);
    } else {
      originalOrder.forEach(row => tbody.appendChild(row));
      renumber(tbody);
      window.toastr.error(window.ifunds.strings.orderSaveFailed);
    }
  });

  tbody.addEventListener('dragend', () => {
    clearIndicators(tbody);
    draggedRow = null;
  });

  // A bare click on the handle must not trigger .row-navigation (see main.ts).
  tbody.addEventListener('click', (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      e.stopPropagation();
    }
  });
}

export default function enhanceReorderableTables(): void {
  document.querySelectorAll<HTMLTableElement>('table[data-reorderable]').forEach(enhanceTable);
}
